import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  postGoesOutAt, preparingSaid, usdToMicro, waitingCounted, waitingSaid, waitingTotal,
  type ActivityTab, type WaitingItem, type WaitingKind, type WaitingResponse,
} from "@joogoos/contracts";
import { api, ApiError } from "../api.js";
import { KICKER } from "../tabs/chrome.js";

/**
 * The morning list ([36](../../../../docs/36-the-morning-list.md), ADR 0064): everything that waits for a yes, read in
 * one go, at the top of the Overview — letters to send, posts to approve, what was made and not posted, articles to
 * read or publish, topics, the Customers loop's proposals, and replies.
 *
 * **Every press is the tool's own route**, with its own owner check, guard and price: Send is the letter's send, Approve
 * is the post's approve, Keep it is the topic's accept. Nothing here can be said yes to that could not be on its tab,
 * and nothing approved here skips a check it would meet there. An article is read before it is approved
 * (ADR 0033 §3), so it is opened rather than approved; a reply is answered in the owner's own inbox.
 *
 * **The marketing review's recommendation is not on it** — it already leads the Overview, as the first thing that
 * needs you and as its card (35), and a screen that says one thing twice is two screens. The email names it, because
 * the email is the only other place.
 *
 * Nothing waiting, nothing drawn: the list is a list of things to do, and an empty one is not news.
 */
export const WAITING_KEY = ["waiting"] as const;

/** On the Overview, not on the email: the review owns its own place on this screen. */
const ELSEWHERE: ReadonlySet<WaitingKind> = new Set(["decision", "todo"]);

export const useWaiting = () => useQuery({ queryKey: WAITING_KEY, queryFn: api.waiting, staleTime: 30_000 });

/** Whether anything this list draws is waiting — the Overview's "All clear" is not said over a list of things to do. */
export const hasWaiting = (w: WaitingResponse | undefined): boolean =>
  Boolean(w?.items.some((i) => !ELSEWHERE.has(i.kind)));

export interface WaitingProps {
  onTab: (tab: ActivityTab) => void;
  onOpen: (projectId: string) => void;
  className?: string;
}

export function Waiting({ onTab, onOpen, className = "" }: WaitingProps) {
  const q = useWaiting();
  const w = q.data;
  if (!w || !hasWaiting(w)) return null;

  const items = w.items.filter((i) => !ELSEWHERE.has(i.kind));
  const counts = Object.fromEntries(Object.entries(w.counts).filter(([k]) => !ELSEWHERE.has(k as WaitingKind)));
  const total = waitingTotal(counts);
  const more = total - items.length;
  const ctx = { gate: w.letters.gate, sendingOn: w.letters.sendingOn, now: new Date() };

  return (
    <section aria-label="Waiting for your yes" className={className}>
      <h2 className={`${KICKER} text-coral`}>Waiting for your yes · {total}</h2>
      <p className="text-[13px] text-muted mt-1.5 leading-relaxed max-w-[60ch]">
        {waitingCounted(counts)}. Nothing goes out until you say yes, and each yes is the same one its own tab gives.
      </p>
      <ul aria-label="Waiting for your yes" className="mt-3 flex flex-col">
        {items.map((item) => (
          <Row key={`${item.kind}:${item.id}`} item={item} said={waitingSaid(item, ctx)} mayDecide={w.mayDecide} onTab={onTab} onOpen={onOpen} />
        ))}
      </ul>
      {more > 0 ? <p className="text-[12px] text-faint mt-2">And {more} more, on their own tabs.</p> : null}
      {w.mayDecide ? <MorningSwitch on={w.morningEmail} /> : null}
    </section>
  );
}

function Row({ item, said, mayDecide, onTab, onOpen }: {
  item: WaitingItem; said: ReturnType<typeof waitingSaid>; mayDecide: boolean;
  onTab: (tab: ActivityTab) => void; onOpen: (projectId: string) => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<Error | null>(null);
  // Every screen that shows one of these rows reads it again: the tool's own tab, and this list.
  const done = () => { setError(null); void qc.invalidateQueries(); };
  const yes = useMutation({ mutationFn: () => sayYes(item), onSuccess: done, onError: setError });
  const no = useMutation({ mutationFn: () => sayNo(item), onSuccess: done, onError: setError });
  const busy = yes.isPending || no.isPending;
  const theirs = said.ownerOnly && !mayDecide;
  const open = () => (item.kind === "made" ? onOpen(item.projectId) : onTab(said.tab));

  return (
    <li className="border-t border-line py-3">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 basis-[280px]">
          <p className="text-[13.5px] font-semibold leading-snug">{said.title}</p>
          <p className="text-[12.5px] text-muted mt-0.5 leading-relaxed break-words">{said.detail}</p>
          {said.note ? <p className="text-[11.5px] text-faint mt-1 leading-relaxed">{said.note}</p> : null}
          {error ? (
            <p role="alert" className="text-[12px] text-bad mt-1 leading-relaxed">
              {error.message}
              {error instanceof ApiError && error.ref ? <span className="opacity-70 font-mono"> · ref {error.ref}</span> : null}
            </p>
          ) : null}
        </div>
        <div className="shrink-0 flex flex-wrap items-center gap-2">
          {theirs ? (
            <span className="text-[11.5px] text-faint">Waits for the owner</span>
          ) : (
            <>
              {said.yes ? (
                <button
                  onClick={() => yes.mutate()} disabled={busy}
                  className="bg-coral text-white rounded-lg px-3 py-1.5 text-[12.5px] font-semibold disabled:opacity-50"
                >
                  {said.yes}
                </button>
              ) : null}
              {said.no ? (
                <button
                  onClick={() => no.mutate()} disabled={busy}
                  className="border border-line rounded-lg px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-line-2 disabled:opacity-50"
                >
                  {said.no}
                </button>
              ) : null}
            </>
          )}
          <button onClick={open} className="text-[12.5px] font-semibold text-coral hover:underline underline-offset-2 px-1 py-1.5">
            {said.open} <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </li>
  );
}

/** Yes, through the route the tool already has. A kind with no yes on this list never reaches here. */
async function sayYes(item: WaitingItem): Promise<unknown> {
  switch (item.kind) {
    case "letter": return api.sendLetter(item.id);
    case "post": return api.approvePost(item.id, { scheduledAt: postGoesOutAt(item, new Date()).toISOString() });
    case "publish": return api.publishArticle(item.articleId);
    case "topic": return api.acceptTopic(item.articleId);
    case "proposal": return api.decideProposal(item.proposalId, "apply");
    case "decision": return api.approveReview(item.reviewId, item.action);
    case "todo": return api.doneReview(item.reviewId);
    default: return undefined;
  }
}

/** No, through the route the tool already has. */
async function sayNo(item: WaitingItem): Promise<unknown> {
  switch (item.kind) {
    case "letter": return api.discardLetter(item.id);
    case "post": return api.rejectPost(item.id);
    case "topic": return api.dropTopic(item.articleId);
    case "proposal": return api.decideProposal(item.proposalId, "reject");
    case "decision": return api.rejectReview(item.reviewId);
    default: return undefined;
  }
}

/**
 * What Joogoos may spend a week preparing this list overnight (36 §the preparer, ADR 0064 point 5), under it.
 *
 * Drawn whether or not anything waits, because it is how the list fills in the first place: a business whose list is
 * empty is exactly the one that may want to turn it on. **The owner's to change, an editor's to read**: it is the
 * business's money, spent with nobody pressing a button that day, so an editor sees the number and who sets it and no
 * way to move it. Zero is off and the default, and the sentence says so rather than showing "$0.00 a week".
 *
 * It reads the list's own answer (`/v1/waiting`), so the Overview asks for nothing more to draw it.
 */
export function Preparing({ className = "" }: { className?: string }) {
  const q = useWaiting();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const save = useMutation({
    mutationFn: (prepareWeekly: string) => api.waitingSettings({ prepareWeekly }),
    onSuccess: () => { setEditing(false); void qc.invalidateQueries({ queryKey: WAITING_KEY }); },
  });
  const weekly = useMutation({
    mutationFn: (reviewWeekly: boolean) => api.waitingSettings({ reviewWeekly }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: WAITING_KEY }); },
  });
  const w = q.data;
  if (!w) return null;
  const p = w.preparing;
  const on = usdToMicro(p.budgetUsd) > 0n;

  return (
    <section aria-label="Prepared overnight" className={className}>
      <p className="text-[12px] text-faint leading-relaxed max-w-[62ch]">
        {preparingSaid(p)}{" "}
        {!w.mayDecide
          ? <span>Only the owner can change it.</span>
          : editing ? null : (
            <button
              onClick={() => { setValue(on ? p.budgetUsd : ""); setEditing(true); save.reset(); }}
              className="font-semibold text-ink-2 underline decoration-line-2 underline-offset-2 hover:text-coral"
            >
              {on ? "Change it" : "Let Joogoos prepare it"}
            </button>
          )}
      </p>
      {/* Only with a week to pay for it: at zero the switch would promise something that cannot run. */}
      {on && w.mayDecide ? (
        <label className="mt-1.5 flex items-center gap-2 text-[12px] text-ink-2">
          <input type="checkbox" checked={p.reviewWeekly} disabled={weekly.isPending} onChange={(e) => weekly.mutate(e.target.checked)} />
          Also write my marketing review every Monday, out of this amount. It only proposes; nothing happens without your yes.
        </label>
      ) : null}
      {editing ? (
        <form
          onSubmit={(e) => { e.preventDefault(); save.mutate(value.trim() || "0"); }}
          className="mt-2 flex flex-wrap items-center gap-2"
        >
          <label className="text-[12.5px] text-ink-2 flex items-center gap-1.5">
            Up to $
            <input
              value={value} onChange={(e) => setValue(e.target.value)} inputMode="decimal" autoFocus
              aria-label="Dollars a week" placeholder="5"
              className="w-20 px-2 py-1 rounded-lg border border-line-2 bg-page text-[12.5px] tabular-nums"
            />
            a week
          </label>
          <button type="submit" disabled={save.isPending} className="px-2.5 py-1 rounded-lg bg-ink text-white text-[11.5px] font-semibold disabled:opacity-50">
            Save
          </button>
          <button type="button" onClick={() => setEditing(false)} className="px-2.5 py-1 rounded-lg border border-line-2 bg-page text-[11.5px] font-medium">
            Cancel
          </button>
          <span className="basis-full text-[11.5px] text-faint leading-relaxed max-w-[62ch]">
            Each night, within it, Joogoos drafts a few posts from your business page for the networks you are on, and,
            if your website takes articles, plans topics and writes the next one you kept. Each one waits here for your
            yes, and the money comes from your credit. 0 stops it.
          </span>
        </form>
      ) : null}
      {save.error ? <p role="alert" className="text-[12px] text-bad mt-1">{save.error.message}</p> : null}
    </section>
  );
}

/** The morning email's switch, under the list it mails: the owner's, so only an owner sees it. */
function MorningSwitch({ on }: { on: boolean }) {
  const qc = useQueryClient();
  const turn = useMutation({
    mutationFn: (morningEmail: boolean) => api.waitingSettings({ morningEmail }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: WAITING_KEY }); },
  });
  return (
    <p className="text-[12px] text-faint mt-3 leading-relaxed">
      {on
        ? "We email you this list in the morning when something new is on it. "
        : "The morning email is off: this list is only here. "}
      <button
        onClick={() => turn.mutate(!on)} disabled={turn.isPending}
        className="font-semibold text-ink-2 underline decoration-line-2 underline-offset-2 hover:text-coral disabled:opacity-50"
      >
        {on ? "Stop the morning email" : "Email it to me in the morning"}
      </button>
      {turn.error ? <span role="alert" className="text-bad"> · {turn.error.message}</span> : null}
    </p>
  );
}
